/**
 * @fileoverview Shared Precedent Template Definitions
 *
 * Template types, Gallagher field schema, and tag utilities shared by
 * Settings > Docs, the Generate Precedents wizard, and the Day One page.
 *
 * Categories and individual templates are now user-defined and stored in
 * the `templateCategories` / `precedentTemplates` Convex tables.
 * Only the Gallagher insurance fields remain hardcoded here.
 */

import { Shield } from "lucide-react";
import type { JobStatus } from "@cvx/jobs/schema";
import {
  isPersonalInsolvencyAppointmentType,
  PERSONAL_INSOLVENCY_APPOINTMENT_TYPES,
} from "@cvx/lib/appointmentTypes";
import { JOB_STATUS_LABELS, JOB_STATUS_ORDER } from "@/lib/job-statuses";
import {
  CONTACT_ROLE_KINDS,
  CONTACT_ROLE_TAG_PREFIX,
  JOB_CONTACT_CUSTOM_ROLE_TAG_PREFIX,
  ROLE_META,
  contactRoleTag,
  jobContactCustomRoleTag,
  parseContactRoleTag,
  parseJobContactCustomRoleTag,
  type ContactRole,
} from "@/lib/contact-roles";

// Re-export for convenience so callers don't have to thread the import
// through two modules. The canonical home is still `@/lib/contact-roles`.
export {
  CONTACT_ROLE_KINDS,
  CONTACT_ROLE_TAG_PREFIX,
  JOB_CONTACT_CUSTOM_ROLE_TAG_PREFIX,
  contactRoleTag,
  jobContactCustomRoleTag,
  parseContactRoleTag,
  parseJobContactCustomRoleTag,
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PrecedentField {
  key: string;
  label: string;
  description?: string;
  exampleValue?: string;
  fieldType: string;
  canonicalKey?: string;
}

export interface PrecedentType {
  key: string;
  name: string;
  shortName: string;
  description: string;
  category: string;
  icon: React.ComponentType<{ className?: string }>;
  requiresSignature?: boolean;
  attachments?: string[];
  stateSpecific?: boolean;
  defaultUrl?: string;
  defaultFileName?: string;
  thirdPartyForm?: boolean;
  /** Only used for web portal forms (Gallagher) that have no PDF to fill */
  hardcodedFields?: PrecedentField[];
}

// ---------------------------------------------------------------------------
// Template type definitions (the 3 fixed types)
// ---------------------------------------------------------------------------

export const TEMPLATE_TYPES = [
  { value: "form" as const, label: "Form (PDF)" },
  { value: "letter" as const, label: "Letter" },
  { value: "insurance" as const, label: "Insurance" },
] as const;

export type TemplateType = (typeof TEMPLATE_TYPES)[number]["value"];

// ---------------------------------------------------------------------------
// Gallagher Insurance — hardcoded field definitions (web portal, not a PDF)
// ---------------------------------------------------------------------------

// Labels mirror the live AJG portal form verbatim (wording, casing and
// punctuation) so the same field reads identically in Morgan's preview, the
// portal-fields config, the generation wizard and the actual portal. Do not
// "tidy up" these strings — they are intentionally a 1:1 copy of the form.
// NOTE: this list is duplicated in convex/settings/precedents/gallagherFields.ts
// (used for backend seeding). Keep the two in sync.
export const GALLAGHER_INSURANCE_FIELDS: PrecedentField[] = [
  {
    key: "appointment_date",
    label: "1. Date of appointment (dd/mm/yyyy)",
    fieldType: "date",
    canonicalKey: "appointment_date",
  },
  {
    key: "firm_name",
    label: "2. Insolvency practitioner (IP) firm",
    fieldType: "text",
    canonicalKey: "firm_name",
  },
  {
    key: "practitioner_name",
    label: "3. Name of appointees",
    fieldType: "text",
    canonicalKey: "practitioner_name",
  },
  {
    key: "contact_name",
    label: "4. IP file manager or primary contact",
    fieldType: "text",
    canonicalKey: "contact_name",
  },
  {
    key: "contact_email",
    label: "4a. Email",
    fieldType: "email",
    canonicalKey: "contact_email",
  },
  {
    key: "firm_phone",
    label: "4b. Direct telephone number",
    fieldType: "phone",
    canonicalKey: "contact_phone",
  },
  {
    key: "mobile_phone",
    label: "4c. Mobile telephone number",
    fieldType: "phone",
    canonicalKey: "mobile_phone",
  },
  {
    key: "administrator_type",
    label: "5. Type Of Appointment",
    fieldType: "text",
    canonicalKey: "administrator_type",
  },
  {
    key: "company_name",
    label: "6. Appointment name",
    fieldType: "text",
    canonicalKey: "appointment_name",
  },
  {
    key: "acn",
    label: "7. Australian/New Zealand Company Number – whichever is applicable",
    fieldType: "text",
    canonicalKey: "acn",
  },
  {
    key: "business_industry",
    label: "8a. What is the Business/ Industry/ Occupation of the Appointment?",
    fieldType: "text",
    canonicalKey: "industry_division",
  },
  {
    key: "business_activities",
    label:
      "8b. Please describe the Activities of the Business/ Industry/ Occupation being managed under the Appointment",
    fieldType: "text",
    canonicalKey: "business_activities",
  },
  {
    key: "general_comments",
    label: "8c. General Comments",
    fieldType: "text",
    canonicalKey: "general_comments",
  },
  {
    key: "principal_risk_address",
    label: "9. Principal risk address",
    fieldType: "address",
    canonicalKey: "registered_office_address",
  },
  {
    key: "trading_status",
    label: "10. Trading status",
    description: "Trading / Not Trading / Ceased Trading",
    fieldType: "text",
  },
  {
    key: "employing_status",
    label: "11. Employing status",
    description: "Employing / Not Employing",
    fieldType: "text",
  },
  {
    key: "locations_by_state",
    label: "12. Locations by state",
    description: "Comma-separated: ACT, NSW, NT, QLD, SA, TAS, VIC, WA, NZ",
    fieldType: "text",
    canonicalKey: "locations_by_state",
  },
  {
    key: "num_employees",
    label: "13. Number of employees or contractors",
    fieldType: "text",
  },
  {
    key: "estimated_asset_value",
    label: "14. Estimated asset value",
    fieldType: "text",
  },
  {
    key: "estimated_appointment_period",
    label: "15. Estimated appointment period",
    fieldType: "text",
  },
  {
    key: "estimated_turnover",
    label: "16. Estimated turnover during appointment",
    fieldType: "text",
  },
  {
    key: "pre_appointment_insurer",
    label: "17. Pre appointment insurer or broker",
    fieldType: "text",
  },
  {
    key: "pre_appointment_workcover",
    label: "18. Pre appointment WorkCover insurer (Australia Only)",
    fieldType: "text",
  },
  {
    key: "crucial_compliance",
    label: "Crucial Compliance Requirement",
    description:
      "Do you require assistance with Health & Safety, Environmental &/or Workplace Relations? (Yes/No)",
    fieldType: "text",
  },
  {
    key: "nominee_1_name",
    label: "Nominee 1 — Name",
    description: "Additional recipient of this notification",
    fieldType: "text",
  },
  { key: "nominee_1_email", label: "Nominee 1 — Email", fieldType: "email" },
  { key: "nominee_2_name", label: "Nominee 2 — Name", fieldType: "text" },
  { key: "nominee_2_email", label: "Nominee 2 — Email", fieldType: "email" },
];

/** The hardcoded Gallagher template definition (used for seeding and field reference) */
export const GALLAGHER_TEMPLATE: PrecedentType = {
  key: "form_gallagher",
  name: "Gallagher Insurance Notification",
  shortName: "Gallagher",
  description: "Insolvency Practitioner Appointment Notification Form",
  category: "insurance",
  icon: Shield,
  thirdPartyForm: true,
  hardcodedFields: GALLAGHER_INSURANCE_FIELDS,
};

// ---------------------------------------------------------------------------
// Template tags — control auto-selection during document generation
// ---------------------------------------------------------------------------

/** Special tag value meaning "include in every generation" */
export const TAG_ALL = "all" as const;

/** Appointment type tags — mirrors APPOINTMENT_TYPES from schema */
export const APPOINTMENT_TYPE_TAGS: { value: string; label: string }[] = [
  { value: "cvl", label: "CVL" },
  { value: "mvl", label: "MVL" },
  { value: "simplified_liquidation", label: "Simplified Liquidation" },
  { value: "court_liquidation", label: "Court Liquidation" },
  { value: "provisional_liquidation", label: "Provisional Liquidation" },
  { value: "sbr", label: "SBR" },
  { value: "voluntary_administration", label: "Voluntary Administration" },
  { value: "deed_of_company_arrangement", label: "DOCA" },
  { value: "receivership", label: "Receivership" },
  { value: "scheme_of_arrangement", label: "Scheme of Arrangement" },
  { value: "personal_bankruptcy", label: "Personal Bankruptcy" },
  { value: "business_bankruptcy", label: "Business Bankruptcy" },
  { value: "creditors_petition", label: "Creditors Petition" },
  { value: "debtors_petition", label: "Debtors Petition" },
  {
    value: "part_x_arrangement",
    label: "Personal Insolvency Agreement (Part X)",
  },
];

/** All pre-defined tag options (for the settings UI tag picker) */
export const ALL_TAG_OPTIONS: { value: string; label: string }[] = [
  { value: TAG_ALL, label: "All Jobs" },
  ...APPOINTMENT_TYPE_TAGS,
];

// ---------------------------------------------------------------------------
// Case type — corporate vs personal insolvency (MOR-470)
// ---------------------------------------------------------------------------

/**
 * High-level case type. Derived from `appointmentType` rather than stored
 * separately: the personal insolvency appointment types (see
 * `convex/lib/appointmentTypes.ts`) ARE the personal insolvency cases;
 * everything else is a corporate appointment. The
 * create-job dialog uses this to gate which appointment types are offered
 * and to adapt field labels/validation.
 */
export type CaseType = "corporate" | "personal";

/** Appointment types that constitute a Personal Insolvency case. */
export const PERSONAL_APPOINTMENT_TYPES = PERSONAL_INSOLVENCY_APPOINTMENT_TYPES;

/** Appointment types that constitute a Corporate appointment (the complement). */
export const CORPORATE_APPOINTMENT_TYPES = APPOINTMENT_TYPE_TAGS.map(
  (t) => t.value,
).filter((v) => !(PERSONAL_APPOINTMENT_TYPES as readonly string[]).includes(v));

/**
 * Derive the case type from an appointment type. Defaults to `"corporate"`
 * when the appointment type is unknown/undefined (the corporate flow is the
 * status quo and the safe default).
 */
export function caseTypeFromAppointmentType(
  appointmentType?: string | null,
): CaseType {
  return isPersonalInsolvencyAppointmentType(appointmentType)
    ? "personal"
    : "corporate";
}

/** Prefix used for custom tag group values stored in the template's tags array */
export const TAG_GROUP_PREFIX = "tg:" as const;

/** Prefix used for job status tags stored in the template's tags array */
export const JOB_STATUS_TAG_PREFIX = "js:" as const;

/** Build the canonical tag string for a job status. */
export function jobStatusTag(status: JobStatus): string {
  return `${JOB_STATUS_TAG_PREFIX}${status}`;
}

/** Job status tag options for the settings UI tag picker (ordered for display). */
export const JOB_STATUS_TAGS: {
  value: string;
  label: string;
  status: JobStatus;
}[] = JOB_STATUS_ORDER.map((status) => ({
  value: jobStatusTag(status),
  label: JOB_STATUS_LABELS[status],
  status,
}));

/**
 * Check whether a template's tags match a given appointment type.
 * Returns true if tags include "all" or the specific appointment type.
 */
export function tagsMatchAppointmentType(
  tags: string[] | undefined,
  appointmentType: string,
): boolean {
  if (!tags || tags.length === 0) return false;
  return tags.includes(TAG_ALL) || tags.includes(appointmentType);
}

/**
 * Full tag matching across all four dimensions: appointment type, job
 * status, custom tag groups, and contact roles.
 *
 * Each dimension is evaluated independently — if the template carries
 * tags in that dimension, the job must satisfy at least one of them; if
 * the template has no tags in that dimension, it's unconstrained.
 *
 * A template matches the job if:
 * 1. The template has at least one tag, AND
 * 2. For every dimension the template is tagged in, the job satisfies
 *    that dimension.
 *
 * Concretely:
 * - Templates tagged only with status `js:day_one` match every Day-One
 *   job regardless of appointment type.
 * - Templates tagged only with appointment type `cvl` match every CVL
 *   job regardless of status.
 * - Templates tagged only with `cr:liability` match every job that has
 *   at least one creditor on its contacts list.
 * - Templates tagged across multiple dimensions must satisfy all of them.
 * - Untagged templates never match (they're never auto-suggested).
 *
 * `jobContactRoles` is the set of distinct `ContactRole`s present
 * on the job's `jobContacts` rows. Pass `undefined` (the default) when
 * the caller hasn't loaded contacts yet — `cr:*` tagged templates will
 * be treated as non-matching to avoid surfacing them prematurely.
 */
export function tagsMatchJob(
  tags: string[] | undefined,
  appointmentType: string | undefined,
  customTagSelections: Record<string, string[]> | undefined,
  jobStatus?: JobStatus,
  jobContactRoles?: ReadonlySet<ContactRole>,
  jobContactCustomRoleIds?: ReadonlySet<string>,
): boolean {
  if (!tags || tags.length === 0) return false;

  const appointmentTagValues = new Set(
    APPOINTMENT_TYPE_TAGS.map((t) => t.value),
  );
  const appointmentTags = tags.filter(
    (t) => t === TAG_ALL || appointmentTagValues.has(t),
  );
  const statusTags = tags.filter((t) => t.startsWith(JOB_STATUS_TAG_PREFIX));
  const customTags = tags.filter((t) => t.startsWith(TAG_GROUP_PREFIX));
  const contactRoleTags = tags.filter((t) =>
    t.startsWith(CONTACT_ROLE_TAG_PREFIX),
  );
  // `jcr:` does not start with `cr:`, so this is disjoint from contactRoleTags.
  const customRoleTags = tags.filter((t) =>
    t.startsWith(JOB_CONTACT_CUSTOM_ROLE_TAG_PREFIX),
  );

  if (
    appointmentTags.length === 0 &&
    statusTags.length === 0 &&
    customTags.length === 0 &&
    contactRoleTags.length === 0 &&
    customRoleTags.length === 0
  ) {
    return false;
  }

  if (appointmentTags.length > 0) {
    if (
      !appointmentTags.includes(TAG_ALL) &&
      (appointmentType === undefined ||
        !appointmentTags.includes(appointmentType))
    ) {
      return false;
    }
  }

  if (statusTags.length > 0) {
    if (!jobStatus) return false;
    const wanted = new Set(
      statusTags.map((t) => t.slice(JOB_STATUS_TAG_PREFIX.length)),
    );
    if (!wanted.has(jobStatus)) return false;
  }

  if (customTags.length > 0) {
    if (!customTagSelections) return false;

    const groupedTags = new Map<string, Set<string>>();
    for (const tag of customTags) {
      const rest = tag.slice(TAG_GROUP_PREFIX.length);
      const colonIdx = rest.indexOf(":");
      if (colonIdx === -1) continue;
      const groupSlug = rest.slice(0, colonIdx);
      const optionValue = rest.slice(colonIdx + 1);
      const group = groupedTags.get(groupSlug) ?? new Set<string>();
      if (!groupedTags.has(groupSlug)) groupedTags.set(groupSlug, group);
      group.add(optionValue);
    }

    for (const [groupSlug, optionValues] of groupedTags) {
      const jobSelections = customTagSelections[groupSlug];
      if (!jobSelections || jobSelections.length === 0) return false;
      const hasMatch = jobSelections.some((sel) => optionValues.has(sel));
      if (!hasMatch) return false;
    }
  }

  if (contactRoleTags.length > 0) {
    if (!jobContactRoles || jobContactRoles.size === 0) return false;
    const wanted = new Set<ContactRole>();
    for (const tag of contactRoleTags) {
      const role = parseContactRoleTag(tag);
      if (role) wanted.add(role);
    }
    if (wanted.size === 0) return false;
    const hasOverlap = [...wanted].some((role) => jobContactRoles.has(role));
    if (!hasOverlap) return false;
  }

  if (customRoleTags.length > 0) {
    if (!jobContactCustomRoleIds || jobContactCustomRoleIds.size === 0) {
      return false;
    }
    const wanted = new Set<string>();
    for (const tag of customRoleTags) {
      const roleId = parseJobContactCustomRoleTag(tag);
      if (roleId) wanted.add(roleId);
    }
    if (wanted.size === 0) return false;
    const hasOverlap = [...wanted].some((id) =>
      jobContactCustomRoleIds.has(id),
    );
    if (!hasOverlap) return false;
  }

  return true;
}

/**
 * Structured "why was this template suggested?" reason. Each kind maps to a
 * single matching dimension; the wizard renders one chip per reason so the
 * user can see at a glance whether a template was picked because of the
 * job's appointment type, its status, a custom tag, a contact role on the
 * job, or a combination.
 *
 * The `appointment_all` kind covers the meta `all` tag (template applies to
 * every appointment type) and is rendered as a generic "Any job" chip.
 *
 * The `contactRole` kind carries the human-readable label so the wizard
 * doesn't have to import `ROLE_META` to render the badge text.
 */
export type MatchReason =
  | { kind: "appointment_all" }
  | { kind: "appointment"; value: string }
  | { kind: "status"; value: JobStatus }
  | { kind: "custom"; groupSlug: string; value: string }
  | { kind: "contactRole"; role: ContactRole; label: string }
  | { kind: "jobContactCustomRole"; roleId: string; label: string };

/**
 * Compute the structured reasons a template's tags match a given job, or
 * `null` if it does not match. Built on top of `tagsMatchJob` so the
 * "matches at all?" decision stays in a single source of truth.
 *
 * Untagged templates never match, so when this function returns an array
 * it always contains at least one reason.
 */
export function getJobMatchReasons(
  tags: string[] | undefined,
  appointmentType: string | undefined,
  customTagSelections: Record<string, string[]> | undefined,
  jobStatus?: JobStatus,
  jobContactRoles?: ReadonlySet<ContactRole>,
  jobContactCustomRoleIds?: ReadonlySet<string>,
  customRoleNames?: ReadonlyMap<string, string>,
): MatchReason[] | null {
  if (
    !tagsMatchJob(
      tags,
      appointmentType,
      customTagSelections,
      jobStatus,
      jobContactRoles,
      jobContactCustomRoleIds,
    )
  ) {
    return null;
  }

  const reasons: MatchReason[] = [];
  if (!tags || tags.length === 0) return reasons;

  if (appointmentType !== undefined && tags.includes(appointmentType)) {
    reasons.push({ kind: "appointment", value: appointmentType });
  } else if (tags.includes(TAG_ALL)) {
    reasons.push({ kind: "appointment_all" });
  }

  if (jobStatus && tags.includes(jobStatusTag(jobStatus))) {
    reasons.push({ kind: "status", value: jobStatus });
  }

  for (const tag of tags) {
    if (!tag.startsWith(TAG_GROUP_PREFIX)) continue;
    const rest = tag.slice(TAG_GROUP_PREFIX.length);
    const colonIdx = rest.indexOf(":");
    if (colonIdx === -1) continue;
    const groupSlug = rest.slice(0, colonIdx);
    const value = rest.slice(colonIdx + 1);
    if (customTagSelections?.[groupSlug]?.includes(value)) {
      reasons.push({ kind: "custom", groupSlug, value });
    }
  }

  if (jobContactRoles && jobContactRoles.size > 0) {
    const tagged = new Set<ContactRole>();
    for (const tag of tags) {
      const role = parseContactRoleTag(tag);
      if (role && jobContactRoles.has(role)) tagged.add(role);
    }
    for (const role of CONTACT_ROLE_KINDS) {
      if (tagged.has(role)) {
        reasons.push({
          kind: "contactRole",
          role,
          label: ROLE_META[role].label,
        });
      }
    }
  }

  if (jobContactCustomRoleIds && jobContactCustomRoleIds.size > 0) {
    const seen = new Set<string>();
    for (const tag of tags) {
      const roleId = parseJobContactCustomRoleTag(tag);
      if (!roleId || seen.has(roleId)) continue;
      if (!jobContactCustomRoleIds.has(roleId)) continue;
      seen.add(roleId);
      reasons.push({
        kind: "jobContactCustomRole",
        roleId,
        label: customRoleNames?.get(roleId) ?? "Custom role",
      });
    }
  }

  return reasons;
}

/**
 * True when the match reasons cover BOTH the job-type dimension (a specific
 * appointment type or the meta `all` tag) AND the job-status dimension.
 *
 * Used by the day-one wizard to narrow auto-suggestion: a template must be
 * tagged with a matching job type *and* a matching status to be suggested.
 * Templates tagged with only one dimension (e.g. just `cvl` with no `js:`
 * status tag) still match `tagsMatchJob` but are not auto-suggested.
 */
export function reasonsCoverTypeAndStatus(reasons: MatchReason[]): boolean {
  const hasType = reasons.some(
    (r) => r.kind === "appointment" || r.kind === "appointment_all",
  );
  const hasStatus = reasons.some((r) => r.kind === "status");
  return hasType && hasStatus;
}
